import { Action, ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import { CheckoutActions } from '../actions';
import { IAddress, IPointOfContact, ITotals } from '../models/common.models';
import { EUserRoles } from '../configurations/common';
import { IRfqBaseInfo } from '../models/rfq.models';
import { IApproverData } from '../models/customer.models';
import { ICartVoucher } from '../models/cart.models';
import { IConsentCheckbox, IUploadedDocument } from '../models/checkout.models';
import { IBaseConfig } from '../models/enviroment-delivery-details.model';

export interface CheckoutState {
  selectedDeliveryAddress: any;
  pointOfContacts: any;
  selectedApprover: any;
  approverComment: string | undefined;
  postCheckoutData: any;
  responseFromPostCheckout: any;
  cartId: string;
  Approvers: [] | undefined;
  ApproversLoaded: boolean;
  NewCustomerAddressData: IAddress | undefined;
  userId: string | undefined;
  ApproversLoadedError: Error | undefined;
  loggedUserRoles: EUserRoles[];
  rfqHistoryInfo: IRfqBaseInfo[] | undefined;
  sapPoNumber: string;
  orderEmailCopy: string | undefined;
  //new state variables for harmonized checkout
  loadingDeliveryDetailsAddressData: boolean;
  availableShipToAddresses: IAddress[] | undefined;
  availableBillToAddresses: IAddress[] | undefined;
  preselectedShipToAddress: IAddress | undefined;
  preselectedBillToAddress: IAddress | undefined;
  deliveryDetailsFormData: IPointOfContact | undefined;
  availableShipmentMethods: IBaseConfig[] | undefined;
  availableShipmentMethodsLoading: boolean;
  deliveryDetailsApproverFormData: IApproverData | undefined;
  deliveryDetailsTotalPrices: ITotals | undefined;
  deliveryDetailsTotalPricesLoading: boolean;
  isSaturdayShipmentSelected: boolean;
  isHeavyweightShipmentSetAutomatically: boolean;
  cartVouchers: ICartVoucher[] | undefined;
  taxNumber: string | null;
  termsAndConditions: IConsentCheckbox[];
  uploadedPoDocuments: IUploadedDocument[];
}

export const initializeState: () => CheckoutState = (): CheckoutState => {
  return {
    responseFromPostCheckout: undefined,
    cartId: '',
    postCheckoutData: undefined,
    approverComment: undefined,
    pointOfContacts: undefined,
    selectedApprover: undefined,
    selectedDeliveryAddress: undefined,
    Approvers: undefined,
    ApproversLoaded: false,
    userId: undefined,
    NewCustomerAddressData: undefined,
    ApproversLoadedError: undefined,
    loggedUserRoles: [EUserRoles.Buyer],
    rfqHistoryInfo: undefined,
    sapPoNumber: '',
    orderEmailCopy: undefined,
    loadingDeliveryDetailsAddressData: true,
    availableShipToAddresses: undefined,
    preselectedShipToAddress: undefined,
    availableBillToAddresses: undefined,
    preselectedBillToAddress: undefined,
    availableShipmentMethods: undefined,
    availableShipmentMethodsLoading: false,
    deliveryDetailsFormData: undefined,
    deliveryDetailsApproverFormData: undefined,
    deliveryDetailsTotalPrices: undefined,
    deliveryDetailsTotalPricesLoading: true,
    isSaturdayShipmentSelected: false,
    isHeavyweightShipmentSetAutomatically: false,
    cartVouchers: [],
    taxNumber: null,
    termsAndConditions: [],
    uploadedPoDocuments: [],
  };
};

const getCheckoutFeatureState: MemoizedSelector<object, CheckoutState> = createFeatureSelector<CheckoutState>('checkout');

export const initialState: CheckoutState = initializeState();

export const selectRfqHistoryData: MemoizedSelector<object, IRfqBaseInfo[] | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.rfqHistoryInfo,
);

export const getSapPoNumber: MemoizedSelector<object, string> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.sapPoNumber,
);

export const getOrderCopyEmail: MemoizedSelector<object, string | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.orderEmailCopy,
);

export const selectLoadingDeliveryDetailsAddressData: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.loadingDeliveryDetailsAddressData,
);

export const selectAvailableShipToAddresses: MemoizedSelector<object, IAddress[] | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.availableShipToAddresses,
);

export const selectPreselectedShipToAddress: MemoizedSelector<object, IAddress | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.preselectedShipToAddress,
);

export const selectIsPreselectedShipToAddressCustom: MemoizedSelector<object, boolean | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.preselectedShipToAddress?.isCustom,
);

export const selectAvailableBillToAddresses: MemoizedSelector<object, IAddress[] | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.availableBillToAddresses,
);

export const selectPreselectedBillToAddress: MemoizedSelector<object, IAddress | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.preselectedBillToAddress,
);

export const selectSelectedShipmentMethod: MemoizedSelector<object, string | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsFormData?.shipmentMethod,
);

export const selectDeliveryDetailsFormData: MemoizedSelector<object, IPointOfContact | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsFormData,
);

export const selectAttentionTo: MemoizedSelector<object, string | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsFormData?.attentionTo,
);

export const selectAvailableShipmentMethods: MemoizedSelector<object, IBaseConfig[] | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.availableShipmentMethods,
);

export const selectAvailableShipmentMethodsLoading: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.availableShipmentMethodsLoading,
);

export const selectDeliveryDetailsApproverFormData: MemoizedSelector<object, IApproverData | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsApproverFormData,
);

export const selectTaxNumberFormData: MemoizedSelector<object, string | null> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.taxNumber,
);

export const selectTermsAndConsentsData: MemoizedSelector<object, IConsentCheckbox[]> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.termsAndConditions,
);

export const selectTotalPrices: MemoizedSelector<object, ITotals | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsTotalPrices,
);

export const selectTotalPricesLoading: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.deliveryDetailsTotalPricesLoading,
);

export const selectIsSaturdayShipmentSelected: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.isSaturdayShipmentSelected,
);

export const selectIsHeavyweightShipmentSetAutomatically: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.isHeavyweightShipmentSetAutomatically,
);

export const selectCartVouchers: MemoizedSelector<object, ICartVoucher[] | undefined> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.cartVouchers,
);

export const selectUploadedPoDocuments: MemoizedSelector<object, IUploadedDocument[]> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.uploadedPoDocuments,
);

export const selectIsAnyPoDocumentUploadedSuccessfully: MemoizedSelector<object, boolean> = createSelector(
  getCheckoutFeatureState,
  (state: CheckoutState) => state.uploadedPoDocuments.some((doc: IUploadedDocument) => doc.uploadSuccess === true),
);

const reducer: ActionReducer<CheckoutState> = createReducer(
  initialState,

  on(CheckoutActions.UpdateCheckoutData, (state: CheckoutState, {
    deliveryAddress,
    pointOfContact,
    approver,
    approverComment,
  }) => {
    return {
      ...state,
      selectedDeliveryAddress: deliveryAddress,
      selectedApprover: approver,
      approverComment,
      pointOfContacts: pointOfContact,
    };
  }),
  on(CheckoutActions.UpdatePOCData, (state: CheckoutState, {payload}) => {
    return {...state, pointOfContacts: payload};
  }),
  on(CheckoutActions.UpdateSelectedDeliveryAddress, (state: CheckoutState, {payload}) => {
    return {...state, selectedDeliveryAddress: payload};
  }),
  on(CheckoutActions.PostCheckoutData, (state: CheckoutState, action): CheckoutState => {
    return {
      ...state,
      postCheckoutData: action.postCheckoutData,
      cartId: action.cartId,
    };
  }),
  on(CheckoutActions.SuccessPostCheckoutDataAction, (state: CheckoutState, action): CheckoutState => {
    return {
      ...state,
      responseFromPostCheckout: action.responseFromPostCheckout,
    };
  }),
  on(CheckoutActions.BeginApproversListAction, (state: CheckoutState) => {
    return {...state};
  }),
  on(CheckoutActions.SuccessGetApproversListAction, (state: CheckoutState, {payload}) => {
    return {...state, Approvers: payload, ApproversLoaded: true};
  }),
  on(CheckoutActions.ErrorGetApproversListAction, (state: CheckoutState, error: Error) => {
    return {...state, ApproversLoadedError: error};
  }),
  on(CheckoutActions.SuccessGetQuoteRfqHistoryDataAction, (state: CheckoutState, {rfqBaseInfo}) => {
    return {
      ...state,
      rfqHistoryInfo: rfqBaseInfo?.data,
    };
  }),
  on(CheckoutActions.setSapPoNumber, (state: CheckoutState, {sapPoNumber}) => {
    return {
      ...state,
      sapPoNumber,
    };
  }),
  on(CheckoutActions.setOrderEmailCopy, (state: CheckoutState, {orderEmailCopy}) => {
    return {
      ...state,
      orderEmailCopy,
    };
  }),
  on(CheckoutActions.clearPurchaseOrderNumber, (state: CheckoutState) => {
    return {
      ...state,
      sapPoNumber: '',
    };
  }),
  on(CheckoutActions.clearOrderEmailCopy, (state: CheckoutState) => {
    return {
      ...state,
      orderEmailCopy: undefined,
    };
  }),
  on(CheckoutActions.setLoadingDeliveryDetailsAddressData, (state: CheckoutState, {loadingDeliveryDetailsAddressData}) => {
    return {
      ...state,
      loadingDeliveryDetailsAddressData: loadingDeliveryDetailsAddressData,
    };
  }),
  on(CheckoutActions.setAvailableShipToAddresses, (state: CheckoutState, {availableShipToAddresses}) => {
    return {
      ...state,
      availableShipToAddresses: availableShipToAddresses,
    };
  }),
  on(CheckoutActions.setAvailableShipToAddressesIfEmpty, (state: CheckoutState, {availableShipToAddresses}) => {
    return {
      ...state,
      availableShipToAddresses: state.availableShipToAddresses ?? availableShipToAddresses,
    };
  }),
  on(CheckoutActions.addShipToAddressToAvailableShipToAddresses, (state: CheckoutState, {shipToAddress}) => {
    return {
      ...state,
      availableShipToAddresses: [...(state.availableShipToAddresses ?? []), shipToAddress],
    };
  }),
  on(CheckoutActions.setPreselectedShipToAddress, (state: CheckoutState, {preselectedShipToAddress}) => {
    return {
      ...state,
      preselectedShipToAddress: preselectedShipToAddress,
      deliveryDetailsFormData: {
        ...state.deliveryDetailsFormData,
        attentionTo: preselectedShipToAddress?.attentionTo ?? ''
      }
    };
  }),
  on(CheckoutActions.setAttentionTo, (state: CheckoutState, {attentionTo}) => {
    return {
      ...state,
      deliveryDetailsFormData: {
        ...state.deliveryDetailsFormData,
        attentionTo: attentionTo
      }
    };
  }),
  on(CheckoutActions.setPreselectedShipToAddressIfNoneIsPreselected, (state: CheckoutState, {preselectedShipToAddress}) => {
    return {
      ...state,
      preselectedShipToAddress: state.preselectedShipToAddress ?? preselectedShipToAddress,
    };
  }),
  on(CheckoutActions.setAvailableBillToAddresses, (state: CheckoutState, {availableBillToAddresses}) => {
    return {
      ...state,
      availableBillToAddresses: availableBillToAddresses,
    };
  }),
  on(CheckoutActions.setPreselectedBillToAddress, (state: CheckoutState, {preselectedBillToAddress}) => {
    return {
      ...state,
      preselectedBillToAddress: preselectedBillToAddress,
    };
  }),
  on(CheckoutActions.setDeliveryDetailsFormData, (state: CheckoutState, {deliveryDetailsFormData}) => {
    return {
      ...state,
      deliveryDetailsFormData: {
        ...state.deliveryDetailsFormData,
        ...deliveryDetailsFormData,
      },
    };
  }),
  on(CheckoutActions.setAvailableShipmentMethods, (state: CheckoutState, {availableShipmentMethods}) => {
    return {
      ...state,
      availableShipmentMethods: availableShipmentMethods,
    };
  }),
  on(CheckoutActions.setAvailableShipmentMethodsLoading, (state: CheckoutState, {availableShipmentMethodsLoading}) => {
    return {
      ...state,
      availableShipmentMethodsLoading: availableShipmentMethodsLoading,
    };
  }),
  on(CheckoutActions.setDeliveryDetailsApproverFormData, (state: CheckoutState, {deliveryDetailsApproverFormData}) => {
    return {
      ...state,
      deliveryDetailsApproverFormData: deliveryDetailsApproverFormData,
    };
  }),
  on(CheckoutActions.setTaxNumberFormData, (state: CheckoutState, {taxNumber}) => {
    return {
      ...state,
      taxNumber: taxNumber,
    };
  }),
  on(CheckoutActions.setTermsAndConsentsData, (state: CheckoutState, {terms}) => {
    return {
      ...state,
      termsAndConditions: terms,
    };
  }),
  on(CheckoutActions.resetDeliveryDetailsFormData, (state: CheckoutState) => {
    return {
      ...state,
      deliveryDetailsFormData: undefined,
    };
  }),
  on(CheckoutActions.resetCartVouchers, (state: CheckoutState) => {
    return {
      ...state,
      cartVouchers: undefined,
    };
  }),
  on(CheckoutActions.resetTaxNumberFormData, (state: CheckoutState) => {
    return {
      ...state,
      taxNumber: null,
    };
  }),
  on(CheckoutActions.resetDeliveryDetailsApproverFormData, (state: CheckoutState) => {
    return {
      ...state,
      deliveryDetailsApproverFormData: undefined,
    };
  }),
  on(CheckoutActions.setTotalPrices, (state: CheckoutState, {totalPrices}) => {
    return {
      ...state,
      deliveryDetailsTotalPrices: totalPrices,
    };
  }),
  on(CheckoutActions.setTotalPricesLoading, (state: CheckoutState, {totalPricesLoading}) => {
    return {
      ...state,
      deliveryDetailsTotalPricesLoading: totalPricesLoading,
    };
  }),
  on(CheckoutActions.setIsSaturdayShipmentSelected, (state: CheckoutState, {isSaturdayShipmentSelected}) => {
    return {
      ...state,
      isSaturdayShipmentSelected: isSaturdayShipmentSelected,
    };
  }),
  on(CheckoutActions.setIsHeavyweightShipmentSetAutomatically, (state: CheckoutState, {isHeavyweightShipmentSetAutomatically}) => {
    return {
      ...state,
      isHeavyweightShipmentSetAutomatically: isHeavyweightShipmentSetAutomatically,
    };
  }),
  on(CheckoutActions.resetIsSaturdayShipmentSelected, (state: CheckoutState) => {
    return {
      ...state,
      isSaturdayShipmentSelected: false,
    };
  }),
  on(CheckoutActions.setCartVouchers, (state: CheckoutState, {cartVouchers}) => {
    return {
      ...state,
      cartVouchers,
    };
  }),
  on(CheckoutActions.clearPreselectedShipToAddress, (state: CheckoutState) => {
    return {
      ...state,
      preselectedShipToAddress: undefined,
    };
  }),
  on(CheckoutActions.clearPreselectedBillToAddress, (state: CheckoutState) => {
    return {
      ...state,
      preselectedBillToAddress: undefined,
    };
  }),
  on(CheckoutActions.addPOUploadDocuments, (state: CheckoutState, {uploadedPoDocuments}) => {
    return {
      ...state,
      uploadedPoDocuments: [...uploadedPoDocuments],
    };
  }),
  on(CheckoutActions.clearPOUploadDocuments, (state: CheckoutState) => {
    return {
      ...state,
      uploadedPoDocuments: [],
    };
  }),
  on(CheckoutActions.setPOUploadDocumentUploadingInProgress, (state: CheckoutState, {fileName, uploadingInProgress}) => {
    const updatedDocuments: IUploadedDocument[] = state.uploadedPoDocuments.map(doc => {
      if (doc.file.name === fileName) {
        return {
          ...doc,
          uploadingInProgress,
        };
      }
      return doc;
    });
    return {
      ...state,
      uploadedPoDocuments: updatedDocuments,
    };
  }),
  on(CheckoutActions.setPOUploadDocumentUploadSuccess, (state: CheckoutState, {fileName, uploadSuccess}) => {
    const updatedDocuments: IUploadedDocument[] = state.uploadedPoDocuments.map(doc => {
      if (doc.file.name === fileName) {
        return {
          ...doc,
          uploadSuccess,
        };
      }
      return doc;
    });
    return {
      ...state,
      uploadedPoDocuments: updatedDocuments,
    };
  }),
  on(CheckoutActions.setPOUploadDocumentUploadError, (state: CheckoutState, {fileName, uploadError}) => {
    const updatedDocuments: IUploadedDocument[] = state.uploadedPoDocuments.map(doc => {
      if (doc.file.name === fileName) {
        return {
          ...doc,
          uploadError,
        };
      }
      return doc;
    });
    return {
      ...state,
      uploadedPoDocuments: updatedDocuments,
    };
  }),
);

export function CheckoutReducers(state: CheckoutState | undefined, action: Action): CheckoutState {
  return reducer(state, action);
}
